import * as React from 'react';
import { useTranslation } from 'react-i18next';
import InfoSpn from 'assets/images/ill_codes_spn.svg';
import InfoSpnFR from 'assets/images/ill_codes_spn_fr.svg';
import InfoFmi from 'assets/images/ill_code_fmi.svg';
import InfoFmiFR from 'assets/images/ill_code_fmi_fr.svg';
import { ReactComponent as EmptyFaultCodesIcon } from 'assets/images/ill_empty_faultcodes.svg';
import { useNavigate, useParams } from 'react-router';
import { ic_nav_back as BackIcon } from 'app/widgets/SVGs';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { NotFoundPage } from 'app/components/NotFoundPage';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import i18next from 'i18next';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { isAbortError } from 'errors/isAbortError';
import { useAuth } from 'context';
import { anonymousAxiosInstance } from 'services/api';
import { BaseTabs, TabPanel } from 'app/components/Tabs';
import './ResourcesFaultCodesPage.less';

import {
  FaultCodesErrorType,
  FaultCodesErrorTypeTabs,
  FaultCodesList,
  FaultCodesSearchInputs,
  SelectedFaultCodePanel,
} from './components';
import clsx from 'clsx';
import { NoResultFound } from 'app/components/access-sharing';
import { useAppSelector } from 'services/store';
import { selectAllModels } from 'services/api/resources/selectors';

export function ResourcesFaultCodesPage() {
  const [searchValue, setSearchValue] = React.useState('');
  const [spnSearchValue, setSpnSearchValue] = React.useState('');
  const [fmiSearchValue, setFmiSearchValue] = React.useState('');
  const [selectedFaultCode, setSelectedFaultCode] = React.useState<any>();
  const [searchValues, setSearchValues] = React.useState<Array<any>>();
  const [selectedErrorType, setSelectedErrorType] =
    React.useState<FaultCodesErrorType>('all');
  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('');
  const [paramsExist, setParamsExist] = React.useState(true);
  const [language, setLanguage] = React.useState(i18next.resolvedLanguage);

  const navigate = useNavigate();
  const params = useParams();
  const { t, i18n } = useTranslation();
  const models = useAppSelector(selectAllModels);
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { equipmentListData } = React.useContext(EquipmentListContext);
  const { isAuthenticated } = useAuth();

  const selectedEquipment = equipmentListData.selectedEquipment;
  const activeCodes = selectedEquipment?.telematics?.faultCodes;
  const model =
    selectedEquipment && params.id != undefined
      ? selectedEquipment.model
      : params.model;

  const equipmentFilter = equipmentListData
    ? equipmentListData.equipmentList?.find(
        equipment => equipment.id === params.id,
      )?.id
    : params.id;

  type FaultCodesModeTab = 'active' | 'lookup';
  const [currentTab, setCurrentTab] = React.useState<FaultCodesModeTab>(() =>
    equipmentFilter ? 'active' : 'lookup',
  );

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  // prettier-ignore
  const infoSpnMap = {
    "en": InfoSpn,
    "fr": InfoSpnFR,
  };

  // prettier-ignore
  const infoFmiMap = {
    "en": InfoFmi,
    "fr": InfoFmiFR,
  };

  React.useEffect(
    () => setLanguage(i18next.resolvedLanguage),
    [i18next.resolvedLanguage],
  );

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      setParamsExist(true);
      if (models && !models.some(c => c.model == model)) {
        setParamsExist(false);
      }
      if (
        (params.id != undefined && params.id != equipmentFilter) ||
        (params.model && params.model != model)
      ) {
        setParamsExist(false);
      }
      setIsLoading(false);
    })();
  }, [i18n.language]);

  // mount
  React.useEffect(() => {
    let index: number = setClassNames('layout-grey');

    return () => removeClassNames(index);
  }, []);

  React.useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchValue]);

  const [searchValueLoading, setSearchValueLoading] = React.useState(false);

  const [falutCodeMobileOpened, setfalutCodeMobileOpened] =
    React.useState(false);

  const isEquipmentContext = Boolean(equipmentFilter);
  const faultCodesWrapperClassName = `relative ${
    falutCodeMobileOpened ? 'fault-code-opened' : 'fault-code-closed'
  }`;

  const hasSearchValue = searchValue.length > 0;
  const hasDebouncedSearchValue = debouncedSearchValue.length > 0;
  const hasSearchValues = Boolean(searchValues);
  const hasSearchResults = Boolean(
    searchValues?.length && searchValues.length > 0,
  );
  const isSearchSyncedWithDebounce = debouncedSearchValue === searchValue;

  const showSearchResults =
    !searchValueLoading &&
    hasSearchValues &&
    hasSearchResults &&
    hasSearchValue;
  const showSearchNoResults =
    !searchValueLoading &&
    hasSearchValues &&
    searchValues?.length === 0 &&
    hasSearchValue &&
    isSearchSyncedWithDebounce;
  const showTypeToStart =
    !searchValueLoading && (!hasSearchValue || !hasDebouncedSearchValue);

  const hasActiveCodes = Boolean(activeCodes?.length && activeCodes.length > 0);
  const searchFaultCodes: any[] = searchValues ?? [];
  const activeFaultCodes: any[] = activeCodes ?? [];

  React.useEffect(() => {
    if (searchValue != '') {
      (async () => {
        setSearchValueLoading(true);
        setSearchValues([]);
        try {
          const response: any = await anonymousAxiosInstance.get(
            `/api/faultCode/${model}?errorType=${selectedErrorType}&code=${searchValue}`,
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
            },
          );

          if (response.status === 200) {
            setSearchValues(response.data?.faultCodes);
          }
        } catch (ex: any) {
          if (isAbortError(ex)) return;

          if (ex.response.status !== 404) {
            addErrorModal();
          }
        } finally {
          setSearchValueLoading(false);
        }
      })();
    } else {
      setSearchValues(undefined);
    }
  }, [debouncedSearchValue]);

  function formatDateTime(date: any) {
    const inputDate = new Date(date);

    const formattedDate = inputDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    const formattedTime = inputDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

    return `${formattedDate} | ${formattedTime}`;
  }

  React.useEffect(() => {
    if (equipmentFilter) {
      setCurrentTab('active');
    }
  }, [equipmentFilter]);

  function resetSearchState() {
    setSelectedErrorType('all');
    setSearchValue('');
    setSelectedFaultCode(null);
    setfalutCodeMobileOpened(false);
    setSpnSearchValue('');
    setFmiSearchValue('');
  }

  function handleErrorTypeSelect(type: FaultCodesErrorType) {
    setSelectedErrorType(type);
    setSearchValue('');
    setSelectedFaultCode(null);
    setfalutCodeMobileOpened(false);
    setSpnSearchValue('');
    setFmiSearchValue('');
  }

  function setJ1939SearchValue(spn: string, fmi: string) {
    if (spn === '' && fmi === '') {
      setSearchValue('');
      return;
    }
    setSearchValue(`${spn !== '' ? spn : '*'}-${fmi !== '' ? fmi : '*'}`);
  }

  const infoSpnSrc = infoSpnMap[language] || InfoSpn;
  const infoFmiSrc = infoFmiMap[language] || InfoFmi;

  function handleBackClick() {
    navigate(-1);
  }

  const tabs: Array<{ label: string; value: FaultCodesModeTab }> = [
    { label: t('active_codes'), value: 'active' },
    { label: t('code_lookup'), value: 'lookup' },
  ];

  function handleTabChange(tab: FaultCodesModeTab) {
    setCurrentTab(tab);
    resetSearchState();
  }

  function handleSearchValueChange(value: string) {
    setSearchValue(value);
    setfalutCodeMobileOpened(false);
  }

  function handleSearchValueClear() {
    setSearchValue('');
    setSelectedFaultCode(null);
    setfalutCodeMobileOpened(false);
  }

  function handleSpnChange(value: string) {
    setSpnSearchValue(value);
    setfalutCodeMobileOpened(false);
    setJ1939SearchValue(value, fmiSearchValue);
  }

  function handleSpnClear() {
    setSpnSearchValue('');
    setfalutCodeMobileOpened(false);
    if (fmiSearchValue != '') {
      setSearchValue(`*-${fmiSearchValue}`);
    } else {
      setSearchValue('');
    }
    setSelectedFaultCode(null);
  }

  function handleFmiChange(value: string) {
    setFmiSearchValue(value);
    setfalutCodeMobileOpened(false);
    setJ1939SearchValue(spnSearchValue, value);
  }

  function handleFmiClear() {
    setFmiSearchValue('');
    setfalutCodeMobileOpened(false);
    if (spnSearchValue != '') {
      setSearchValue(`${spnSearchValue}-*`);
    } else {
      setSearchValue('');
    }
    setSelectedFaultCode(null);
  }

  function handleSelectFaultCode(faultCode: any) {
    setSelectedFaultCode(faultCode);
    setfalutCodeMobileOpened(true);
  }

  function handleMobileBack() {
    setfalutCodeMobileOpened(false);
  }

  function handleContactDealer() {
    navigate('/dealers');
  }

  if (!paramsExist) {
    return <NotFoundPage />;
  }

  if (isLoading) {
    return (
      <div className="loading-full-page">
        <div className="loading">
          <div className="loading_text">{t('loading')}...</div>
          <div className="loading_icon">
            <div className="css-icon"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={isEquipmentContext ? 'layout-info-panel' : ''}>
        <div className={faultCodesWrapperClassName}>
          <span
            className="back-button back-button-on-layout fixed-on-desktop"
            onClick={handleBackClick}
          >
            <BackIcon />
          </span>

          <div className="box-content fault-codes box-content-full-height">
            <div className="row">
              <div
                className={clsx('col-sm-12', {
                  'col-sm-6': !!selectedFaultCode,
                })}
              >
                <h3 className="fault-code-name">
                  {selectedEquipment?.nickName || model}
                </h3>

                <h2>{t('fault_code_screen_title')}</h2>

                <div className="separator-m"></div>
                {isEquipmentContext && (
                  <>
                    <BaseTabs
                      className="resources-fault-codes-page__tabs"
                      tabs={tabs}
                      value={currentTab}
                      onChange={handleTabChange}
                    />
                  </>
                )}

                <TabPanel value={'active'} currentTab={currentTab}>
                  {hasActiveCodes ? (
                    <div
                      className={clsx({
                        'col-sm-6': !selectedFaultCode,
                        'col-sm-12': selectedFaultCode,
                      })}
                    >
                      <FaultCodesList
                        className="search-results-fault-codes search-results-fault-codes-active"
                        faultCodes={activeFaultCodes}
                        selectedFaultCode={selectedFaultCode}
                        t={t}
                        showReportedTime={true}
                        formatDateTime={formatDateTime}
                        onSelect={handleSelectFaultCode}
                      />
                    </div>
                  ) : (
                    <NoResultFound
                      className="resources-fault-codes-page__no-result-found"
                      icon={EmptyFaultCodesIcon}
                      title={t('no_active_fault_codes')}
                    />
                  )}
                </TabPanel>

                <TabPanel value={'lookup'} currentTab={currentTab}>
                  <div
                    className={clsx({
                      'col-sm-6': !selectedFaultCode,
                      'col-sm-12': selectedFaultCode,
                    })}
                  >
                    <FaultCodesErrorTypeTabs
                      selectedErrorType={selectedErrorType}
                      onSelect={handleErrorTypeSelect}
                      t={t}
                    />

                    <FaultCodesSearchInputs
                      selectedErrorType={selectedErrorType}
                      searchValue={searchValue}
                      spnSearchValue={spnSearchValue}
                      fmiSearchValue={fmiSearchValue}
                      language={language}
                      infoSpnSrc={infoSpnSrc}
                      infoFmiSrc={infoFmiSrc}
                      t={t}
                      onSearchValueChange={handleSearchValueChange}
                      onSearchValueClear={handleSearchValueClear}
                      onSpnChange={handleSpnChange}
                      onSpnClear={handleSpnClear}
                      onFmiChange={handleFmiChange}
                      onFmiClear={handleFmiClear}
                    />

                    {showSearchResults && (
                      <div>
                        <h4 className="results-found-title">
                          {t('search_results')}:
                        </h4>
                        <FaultCodesList
                          className="search-results-fault-codes"
                          faultCodes={searchFaultCodes}
                          selectedFaultCode={selectedFaultCode}
                          t={t}
                          onSelect={handleSelectFaultCode}
                        />
                      </div>
                    )}

                    {searchValueLoading && (
                      <div>
                        <div className="loading results-found-title">
                          <div className="loading_text">{t('loading...')}</div>
                          <div className="loading_icon">
                            <div className="css-icon"></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {showTypeToStart && (
                      <div>
                        <h4 className="results-found-title">
                          {t('type_in_searchbar_to_start')}
                        </h4>
                      </div>
                    )}
                  </div>

                  {showSearchNoResults && (
                    <NoResultFound
                      className="resources-fault-codes-page__no-result-found"
                      title={t('no_results_found')}
                      description={t('no_result_for', { s: searchValue })}
                    />
                  )}
                </TabPanel>
              </div>

              <SelectedFaultCodePanel
                selectedFaultCode={selectedFaultCode}
                isAuthenticated={isAuthenticated}
                t={t}
                onMobileBack={handleMobileBack}
                onContactDealer={handleContactDealer}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
